import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Button, Checkbox, FormControlLabel, TextField, Typography,
  InputAdornment, IconButton, CircularProgress, Alert, GlobalStyles,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { login } from "./UserAPI";
import axiosInstance from "../../../services/axiosInstance";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../../actions/authSlice";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import logoImg  from "../../../../design-system/assets/brand/logo.png";
import teliWave from "../../../../design-system/assets/mascot/teli-wave.png";
import bgSky from "../../../../design-system/assets/bg-sky.png";
import { LanguageSwitcherCompact } from "../../../i18n/LanguageSwitcher.jsx";

/* ── Validation ──────────────────────────────────────────────────────────── */
const schema = Yup.object({
  email: Yup.string().email("Invalid email address").required("Email is required"),
  password: Yup.string().min(8, "Min 8 characters").required("Password is required"),
});

/* ── Shared input style (matches mockup: 44px, 12px radius, #E7E9F4 border) */
const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "var(--r-md)",          // 12px
    backgroundColor: "#fff",
    fontFamily: "var(--font-ui)",
    "& input": {
      fontFamily: "var(--font-ui)",
      fontSize: "var(--t-body)",           // 15px
      color: "var(--ink-800)",
      padding: "0 14px",
    },
    "& fieldset": { borderColor: "var(--line-200)" },
    "&:hover fieldset": { borderColor: "var(--line-300)" },
    "&.Mui-focused fieldset": { borderColor: "var(--line-200)", borderWidth: "1px" },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "var(--font-ui)",
    fontSize: "var(--t-caption)",
    marginInline: 0,
  },
};


/* ================================================================
   SignIn — login-page.md §7.1 + reference/target-login.png
   Visual rebrand only — auth flow, Formik, JWT, routes UNCHANGED.

   Structure matches Teleredo Mockup.dc.html exactly:
   - 2-column grid (1fr 1fr) on md+, single column on mobile
   - Left: grid/place-items:center, 40px padding, single card
   - Logo IS INSIDE the card at the top (mb:24px) per mockup
   - Right: mascot bottom-aligned, height:88vh (% fails on min-height grids)
   ================================================================ */
const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation(["auth", "common"]);
  const [showPassword, setShowPassword]   = useState(false);
  const [authError, setAuthError]         = useState("");
  // OTP verification step
  const [step, setStep]                   = useState("credentials"); // "credentials" | "verify"
  const [loginToken, setLoginToken]       = useState("");
  const [otpCode, setOtpCode]             = useState("");
  const [otpError, setOtpError]           = useState("");
  const [otpLoading, setOtpLoading]       = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const onSubmit = async (values, { setSubmitting }) => {
    setAuthError("");
    try {
      const result = await login({ UserName: values.email, Password: values.password });
      const data = result.data;
      if (data.requiresVerification) {
        // Superuser with email — go to OTP step
        setLoginToken(data.loginToken);
        setStep("verify");
        startResendCooldown();
      } else {
        // Direct token (sub-account or no email on record)
        dispatch(setCredentials({ token: data.token }));
        navigate("../sidebar");
      }
    } catch {
      setAuthError("Invalid email or password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(s => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const onVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError("Please enter the 6-digit code.");
      return;
    }
    setOtpError("");
    setOtpLoading(true);
    try {
      const { data } = await axiosInstance.post("/auth/login/verify", { loginToken, code: otpCode });
      dispatch(setCredentials({ token: data.token }));
      navigate("../sidebar");
    } catch (e) {
      setOtpError(e.response?.data?.message || "Verification failed. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <>
    <GlobalStyles styles={`
      @keyframes teliFloat {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      @media (prefers-reduced-motion: reduce) {
        .teli-float { animation: none !important; }
      }
    `} />
    {/* Background recipe per backgrounds.md §4.2:
       CSS gradient base (uniform lavender) + brand radial halos.
       bg-clouds.png is a dense 1448×1086 PNG — used as an absolutely-positioned
       overlay at 0.18 opacity so it blends softly without tiling artifacts. */}
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
      minHeight: "100dvh",
      position: "relative",
      overflow: "hidden",
      backgroundImage: `url(${bgSky})`,
      backgroundSize: "cover",
      backgroundPosition: "center top",
      backgroundRepeat: "no-repeat",
    }}>

      {/* Language switcher — top-right corner of the full-page layout */}
      <Box sx={{ position: "absolute", top: 16, insetInlineEnd: 16, zIndex: 10 }}>
        <LanguageSwitcherCompact variant="light" />
      </Box>

      {/* ── LEFT PANEL ─────────────────────────────────────────────────────── */}
      <Box sx={{
        display: "grid",
        placeItems: "center end",
        padding: { xs: "24px", sm: "40px 24px 40px 40px" },
        overflowY: "auto",
        alignContent: "center",
        position: "relative",
        zIndex: 1,
      }}>

        {/* Card — logo INSIDE per canonical mockup */}
        <Box sx={{
          width: "100%",
          maxWidth: 420,
          background: "rgba(255,255,255,.96)",          // mockup: rgba(255,255,255,.96)
          borderRadius: "var(--r-xl)",                  // 20px
          boxShadow: "0 16px 48px rgba(46,55,120,.14)", // mockup exact
          padding: "40px",
        }}>

          {/* Logo */}
          <Box
            component="img"
            src={logoImg}
            alt="Teleredo"
            role="button"
            tabIndex={0}
            sx={{ height: 34, width: "auto", mb: "24px", display: "block", cursor: "pointer" }}
            onClick={() => navigate("/")}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate("/")}
          />

          {/* ── STEP 1: Credentials ─────────────────────────────────────────── */}
          {step === "credentials" && (<>
            <Typography component="h1" sx={{
              fontFamily: "var(--font-display)",
              fontSize: "1.875rem", fontWeight: 800,
              color: "var(--ink-900)", letterSpacing: "-0.02em", lineHeight: 1.2, mb: 0,
            }}>
              {t("auth:welcomeBack")}
            </Typography>
            <Typography sx={{
              fontSize: "0.875rem", color: "var(--ink-500)", mt: "6px", mb: "26px", lineHeight: 1.5,
            }}>
              {t("auth:enterCredentials")}
            </Typography>

            {authError && (
              <Alert severity="error" variant="outlined" onClose={() => setAuthError("")}
                sx={{ mb: 2.5, borderRadius: "var(--r-md)", fontSize: "var(--t-caption)" }}>
                {authError}
              </Alert>
            )}

            <Formik
              initialValues={{ email: "", password: "", remember: false }}
              validationSchema={schema}
              onSubmit={onSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form noValidate>
                  <Box sx={{ mb: "16px" }}>
                    <Typography component="label" htmlFor="email" sx={{
                      display: "block", fontSize: "var(--t-caption)", fontWeight: 600,
                      color: "var(--ink-600)", mb: "6px",
                    }}>{t("auth:email")}</Typography>
                    <TextField fullWidth hiddenLabel id="email" name="email"
                      type="email" autoComplete="email" placeholder={t("auth:emailPlaceholder")}
                      value={values.email} onChange={handleChange} onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email} sx={inputSx} />
                  </Box>

                  <Box sx={{ mb: "14px" }}>
                    <Typography component="label" htmlFor="password" sx={{
                      display: "block", fontSize: "var(--t-caption)", fontWeight: 600,
                      color: "var(--ink-600)", mb: "6px",
                    }}>{t("auth:password")}</Typography>
                    <TextField fullWidth hiddenLabel id="password" name="password"
                      type={showPassword ? "text" : "password"} autoComplete="current-password"
                      value={values.password} onChange={handleChange} onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password} sx={inputSx}
                      InputProps={{ endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPassword(p => !p)}
                            edge="end" tabIndex={-1}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            sx={{ color: "var(--ink-400)", mr: "-4px" }}>
                            {showPassword
                              ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                              : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      ) }} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "22px" }}>
                    <FormControlLabel
                      control={<Checkbox name="remember" checked={values.remember} onChange={handleChange}
                        sx={{ width: 17, height: 17, padding: 0, marginInlineEnd: "8px",
                          color: "var(--ink-400)", "&.Mui-checked": { color: "var(--brand-violet)" },
                          "& .MuiSvgIcon-root": { fontSize: 17 } }} />}
                      label={<Typography sx={{ fontSize: "0.84375rem", color: "var(--ink-600)" }}>{t("auth:rememberMe")}</Typography>}
                      sx={{ margin: 0, gap: 0 }} />
                    <Link
                      to="/forgot-password"
                      state={{ identifier: values.email }}
                      style={{ color: "var(--brand-violet-ink)", fontWeight: 600, fontSize: "0.84375rem", textDecoration: "none" }}>
                      {t("auth:forgotPassword")}
                    </Link>
                  </Box>

                  <Button type="submit" fullWidth variant="contained" disabled={isSubmitting}
                    sx={{
                      height: 48, mb: "20px", borderRadius: "var(--r-md)",
                      background: "var(--grad-brand-135)",
                      boxShadow: "0 8px 20px rgba(90,70,220,.28)",
                      fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--t-body)",
                      color: "#fff", textTransform: "none", border: "none",
                      "&:hover": { filter: "brightness(1.06)", boxShadow: "0 8px 20px rgba(90,70,220,.28)" },
                      "&:active": { transform: "translateY(1px)", transition: "transform .1s" },
                      "&.Mui-disabled": { background: "var(--grad-brand-135)", color: "rgba(255,255,255,.5)" },
                    }}>
                    {isSubmitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t("auth:signIn")}
                  </Button>
                </Form>
              )}
            </Formik>

            <Typography sx={{ textAlign: "center", fontSize: "0.84375rem", color: "var(--ink-500)", mt: "24px" }}>
              {t("auth:newToTeleredo")}{" "}
              <Link to="/signup" style={{ color: "var(--brand-violet-ink)", fontWeight: 600, textDecoration: "none" }}>
                {t("auth:createAccount")}
              </Link>
            </Typography>
          </>)}

          {/* ── STEP 2: OTP verification ────────────────────────────────────── */}
          {step === "verify" && (<>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                background: "rgba(79,70,229,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <LockOutlinedIcon sx={{ fontSize: 22, color: "#4F46E5" }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--ink-900)", letterSpacing: "-0.01em" }}>
                  {t("auth:verifyIdentity")}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: "var(--ink-500)", lineHeight: 1.4 }}>
                  {t("auth:codeSentToEmail")}
                </Typography>
              </Box>
            </Box>

            {otpError && (
              <Alert severity="error" variant="outlined" onClose={() => setOtpError("")}
                sx={{ mb: 2, borderRadius: "var(--r-md)", fontSize: "var(--t-caption)" }}>
                {otpError}
              </Alert>
            )}

            <Box sx={{ mb: "14px" }}>
              <Typography component="label" htmlFor="otpCode" sx={{
                display: "block", fontSize: "var(--t-caption)", fontWeight: 600,
                color: "var(--ink-600)", mb: "6px",
              }}>{t("auth:verificationCode")}</Typography>
              <TextField fullWidth hiddenLabel id="otpCode" name="otpCode"
                inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
                placeholder="000000"
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={e => e.key === "Enter" && onVerifyOtp()}
                autoFocus
                sx={{
                  ...inputSx,
                  "& .MuiOutlinedInput-root input": {
                    textAlign: "center", letterSpacing: "0.35em",
                    fontSize: "1.375rem", fontWeight: 700, fontFamily: "monospace",
                  },
                }} />
            </Box>

            <Button fullWidth variant="contained" disabled={otpLoading || otpCode.length !== 6}
              onClick={onVerifyOtp}
              sx={{
                height: 48, mb: "16px", borderRadius: "var(--r-md)",
                background: "var(--grad-brand-135)",
                boxShadow: "0 8px 20px rgba(90,70,220,.28)",
                fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--t-body)",
                color: "#fff", textTransform: "none", border: "none",
                "&:hover": { filter: "brightness(1.06)" },
                "&.Mui-disabled": { background: "var(--grad-brand-135)", color: "rgba(255,255,255,.5)" },
              }}>
              {otpLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t("auth:verifyAndSignIn")}
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Button size="small" onClick={() => { setStep("credentials"); setOtpCode(""); setOtpError(""); }}
                sx={{ fontSize: "0.8125rem", color: "var(--ink-500)", textTransform: "none", p: 0 }}>
                {t("auth:backToLogin")}
              </Button>
              <Button size="small" disabled={resendCooldown > 0}
                onClick={async () => {
                  try {
                    await axiosInstance.post("/auth/login/resend", { loginToken });
                    startResendCooldown();
                  } catch {}
                }}
                sx={{ fontSize: "0.8125rem", color: resendCooldown > 0 ? "var(--ink-400)" : "var(--brand-violet-ink)", textTransform: "none", p: 0 }}>
                {resendCooldown > 0 ? t("auth:resendIn", { count: resendCooldown }) : t("auth:resendCode")}
              </Button>
            </Box>
          </>)}

        </Box>
      </Box>

      {/* ── RIGHT PANEL ────────────────────────────────────────────────────── */}
      <Box sx={{
        display: { xs: "none", md: "grid" },
        placeItems: "end start",
        paddingLeft: "16px",
        overflow: "hidden",
        position: "relative",
        zIndex: 1,
      }}>
        {/* Soft glow blobs — reference PNG shows subtle ambient violet/cyan behind mascot.
            Canonical mockup HTML omits them but reference PNG confirms they should be present.
            Kept at very low opacity to match the subtle look in the reference. */}
        <Box sx={{
          position: "absolute",
          top: "15%", left: "50%",
          transform: "translateX(-50%)",
          width: 560, height: 560,
          borderRadius: "50%",
          background: "radial-gradient(closest-side, rgba(122,63,228,0.45), transparent)",
          filter: "blur(90px)",
          opacity: 0.18,
          pointerEvents: "none",
          zIndex: 0,
        }} />
        <Box sx={{
          position: "absolute",
          top: "40%", right: "5%",
          width: 420, height: 420,
          borderRadius: "50%",
          background: "radial-gradient(closest-side, rgba(31,192,218,0.40), transparent)",
          filter: "blur(80px)",
          opacity: 0.14,
          pointerEvents: "none",
          zIndex: 0,
        }} />

        {/* Mascot — height:88vh (% fails on min-height grid; vh always resolves) */}
        <Box
          component="img"
          src={teliWave}
          alt="Teli, your Teleredo AI assistant"
          className="teli-float"
          sx={{
            position: "relative",
            zIndex: 2,
            height: "88vh",
            width: "auto",
            display: "block",
            filter: "drop-shadow(0 22px 34px rgba(46,55,120,.16))",
            animation: "teliFloat var(--float-loop, 6s) ease-in-out infinite",
          }}
        />
      </Box>

    </Box>
    </>
  );
};

export default SignIn;
